import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, CalendarPlus, Bell, Monitor, Gauge, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { EVENTO } from "./WorkshopLanding";

// 27 ott 2026, 20:30 CET (19:30 UTC) → 90 min
const GCAL =
  "https://calendar.google.com/calendar/render?action=TEMPLATE" +
  "&text=" + encodeURIComponent("Workshop con Massimiliano Pulla — Network marketing online") +
  "&dates=20261027T193000Z/20261027T210000Z" +
  "&details=" + encodeURIComponent("Workshop gratuito in diretta su Zoom. Riceverai il link di accesso via email.") +
  "&location=" + encodeURIComponent("Online su Zoom");

const semaforoHref = (state) => {
  const p = new URLSearchParams({ from: "workshop" });
  if (state?.nome) p.set("nome", state.nome);
  if (state?.email) p.set("email", state.email);
  if (state?.telefono) p.set("tel", state.telefono);
  return `/semaforo?${p.toString()}`;
};

export default function WorkshopThankYou() {
  const { state } = useLocation();
  const nome = state?.nome;

  useEffect(() => {
    document.title = "Grazie — Workshop Network marketing online";
  }, []);

  useEffect(() => {
    // Evento di conversione Meta Pixel: una sola volta per sessione (niente doppi su refresh/StrictMode).
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    try {
      if (sessionStorage.getItem("ws_lead_fired")) return;
      sessionStorage.setItem("ws_lead_fired", "1");
    } catch { /* storage bloccato */ }
    window.fbq("track", "Lead", { content_name: EVENTO });
  }, []);

  return (
    <div className="min-h-screen bg-[#0B1120] text-white font-body antialiased" data-testid="thankyou-page">
      <header className="border-b border-[#1E2A44]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/workshop" className="flex items-center gap-2.5" data-testid="ty-wordmark">
            <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
            <span className="font-display font-extrabold tracking-[0.16em] text-sm uppercase">Massimiliano Pulla</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-14 sm:py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="w-20 h-20 rounded-full bg-[#34D399]/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={44} className="text-[#34D399]" />
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl mb-4 text-balance" data-testid="ty-title">
            {nome ? `Ci siete, ${nome}!` : "Ci siete!"}
          </h1>
          <p className="text-[#94A3B8] text-base sm:text-lg leading-relaxed text-pretty max-w-lg mx-auto">
            Vi ho salvato il posto per il workshop di{" "}
            <span className="text-[#F59E0B] font-semibold">martedì 27 ottobre, alle 20:30</span>.
            <br />
            Adesso il regalo: due minuti, e martedì arrivate sapendo di che colore siete.
          </p>

          <Link
            to={semaforoHref(state)}
            data-testid="thankyou-semaforo"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] text-[#0B1120] font-bold text-base px-7 py-4 rounded-full transition-colors"
          >
            <Gauge size={18} /> Fate subito il Semaforo <ArrowRight size={18} />
          </Link>

          <p className="mt-5 text-sm text-[#64748B]" data-testid="thankyou-semaforo-hint">
            Cinque domande a fasce, un minuto: alla fine vedete il vostro colore, i mesi di ossigeno e ricevete il vostro Piano Ossigeno (PDF).
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 text-left bg-[#141F36] border border-[#1E2A44] rounded-xl p-6 sm:p-8"
        >
          <h2 className="font-display font-bold text-xl mb-6 text-center">Cosa succede adesso</h2>
          <ul className="space-y-6">
            <li className="flex gap-4" data-testid="ty-step-email">
              <Bell className="text-[#F59E0B] shrink-0 mt-0.5" size={22} />
              <div>
                <p className="font-semibold">Controllate email e WhatsApp</p>
                <p className="text-sm text-[#94A3B8] mt-1 text-pretty">
                  Vi arrivano il link di accesso e qualche promemoria prima della serata. Se non vedete l'email, guardate anche nello spam.
                </p>
              </div>
            </li>
            <li className="flex gap-4" data-testid="ty-step-cal">
              <CalendarPlus className="text-[#F59E0B] shrink-0 mt-0.5" size={22} />
              <div>
                <p className="font-semibold">Salvate la data</p>
                <p className="text-sm text-[#94A3B8] mt-1 mb-3 text-pretty">
                  Aggiungete il workshop al vostro calendario, così non ve lo dimenticate.
                </p>
                <a
                  href={GCAL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ty-addcal"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#F59E0B] border border-[#F59E0B]/40 rounded-full px-4 py-2 hover:bg-[#F59E0B]/10 transition-colors"
                >
                  <CalendarPlus size={15} /> Aggiungi al calendario
                </a>
              </div>
            </li>
            <li className="flex gap-4" data-testid="ty-step-zoom">
              <Monitor className="text-[#F59E0B] shrink-0 mt-0.5" size={22} />
              <div>
                <p className="font-semibold">Vi servirà solo Zoom</p>
                <p className="text-sm text-[#94A3B8] mt-1 text-pretty">
                  Il workshop è in diretta su Zoom: basta un telefono o un computer con una connessione a internet.
                </p>
              </div>
            </li>
          </ul>
        </motion.div>

        <p className="mt-10 text-[#64748B] text-sm">Ci vediamo lì. — Massimiliano</p>
      </main>
    </div>
  );
}
